// The client's request for a quote (RFQ), recognised by what the text says
// (CN2, Peter 2026-10-13: "file names shouldnt dictate anything. only the
// contents.").
//
// Before CN2 a file NAME ("RFQ.pdf", "Quote Request.pdf", "Work Order 4172.pdf")
// or the "01. RFQ Package" folder an email was filed in could decide which
// document was the RFQ. That decision opens the RFQ gate, picks the document
// the client details are read from and labels a document "authoritative client
// request" in the final quote prompt. This module reads the text alone. A file
// name, an extension and a folder are never inputs.
//
// Three kinds of request are recognised:
//   - 'formal-form'    a titled request: a quote or quotation request, a request
//                      for quotation, an invitation to tender, a work,
//                      purchase, service or job order, or a tender package
//                      (conditions of tendering, a tender period);
//   - 'strata-request' a strata or building manager's request without a title:
//                      it asks for a price and carries strata markers (owners
//                      corporation, strata plan, committee, site contact);
//   - 'email-ask'      a short message that asks for a quote ("Please quote to
//                      repair the balcony at unit 4").
//
// It never fires on (checked first, whatever the text also says):
//   - a quote or tender anyone sends, or its cover note ("has pleasure in
//     submitting", "Please find attached our quote", totals, validity);
//   - a document that opens with a builder's letterhead (a licence number);
//   - the office's own site inspection request to its assessor;
//   - an inspection, engineering or site report, including one that quotes the
//     request it answers;
//   - the quoting business replying to a request;
//   - an invoice or statement;
//   - a drawing or plan;
//   - the estimator's own priced working notes (detectEstimatorPricedNotes).
//
// Several rules were added after reading RJ Bird's documents on this PC
// (docs/contents-not-names/CN2-results-2026-10-14.md): the builder letterhead,
// the report title at the start, the quoting business's reply, the tender
// package, the title-position rule and the money-figure count.
//
// Pure and synchronous. Reads the first RFQ_SAMPLE_CHARS characters.

import { detectEstimatorPricedNotes } from './estimator-notes-detector';

/** How much of a text is read, the same window the document classifier reads. */
export const RFQ_SAMPLE_CHARS = 8000;

export type RfqContentKind = 'formal-form' | 'strata-request' | 'email-ask';
export type RfqContentRejection = 'estimator-notes' | 'quote' | 'builder-document' | 'report' | 'inspection-request' | 'reply' | 'invoice' | 'plan';

export interface RfqContentResult {
    isRfq: boolean;
    kind: RfqContentKind | null;
    /** Why the text is not a request, when a rejection decided it. */
    rejectedAs: RfqContentRejection | null;
    /** The request's own title ("QUOTE REQUEST", "Work Order"), when it has one. */
    title: string | null;
    /** True for a work, purchase, service or job order: the client's authority to proceed. */
    authority: boolean;
    evidence: string[];
}

// ─── Reading the text ───────────────────────────────────────────────────────

// How far into the text a title or heading is looked for.
const HEAD_CHARS = 700;
// A request that is not titled asks near its start, unless it is an email.
const ASK_WINDOW_CHARS = 1500;
// A report or an inspection request names itself near its start.
const TITLE_CHARS = 400;

function normalise(text: string): string {
    return text
        .replace(/\r\n?/g, '\n')
        .slice(0, RFQ_SAMPLE_CHARS)
        // PDF page markers the importer writes between pages.
        .replace(/^[ \t]*-{2,}\s*page\s+\d+\s*-{2,}[ \t]*$/gim, '')
        // Letter-spaced capitals from some PDF forms: "Q U O T A T I O N".
        .replace(/\b(?:[A-Z] ){3,}[A-Z]\b/g, match => match.replace(/ /g, ''));
}

function firstMatch(text: string, patterns: RegExp[]): string | null {
    for (const pattern of patterns) {
        const match = text.match(pattern);
        if (match) return match[0].replace(/\s+/g, ' ').trim();
    }
    return null;
}

// The same words with any whitespace between them, to find a title again.
function wordsPattern(words: string): RegExp {
    return new RegExp(words.trim().split(/\s+/).map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('\\s+'), 'i');
}

function earliestMatch(text: string, patterns: RegExp[]): { text: string; index: number } | null {
    let best: { text: string; index: number } | null = null;
    for (const pattern of patterns) {
        const match = text.match(pattern);
        if (match && match.index !== undefined && (!best || match.index < best.index)) {
            best = { text: match[0].replace(/\s+/g, ' ').trim(), index: match.index };
        }
    }
    return best;
}

function countMatches(text: string, patterns: RegExp[]): string[] {
    return patterns.map(pattern => text.match(pattern)?.[0]).filter((value): value is string => Boolean(value))
        .map(value => value.replace(/\s+/g, ' ').trim());
}

// ─── Email shape ────────────────────────────────────────────────────────────

const EMAIL_HEADER_LINE_RE = /^[ \t>]*(?:from|to|sent|date|subject|cc)\s*:/gim;
const GREETING_RE = /(?:^|\n)[ \t]*(?:subject\s*:[^\n]*\n[ \t]*)?(?:hi|hello|hey|dear|good\s+(?:morning|afternoon|evening)|g'day)\b/i;
const SIGN_OFF_RE = /\b(?:kind(?:est)?\s+regards|best\s+regards|warm\s+regards|many\s+thanks|regards|thanks(?:\s+(?:so\s+much|heaps|again))?|thank\s+you|cheers|yours\s+(?:sincerely|faithfully|truly))\b/i;

// The importer writes a decoded email as its header lines, then the body.
const LEADING_HEADER_RE = /^\s*(?:subject|from)\s*:/i;

// An email by its headers or its greeting (a sign-off alone is not enough).
function addressedEmail(text: string): boolean {
    const headers = (text.slice(0, 2000).match(EMAIL_HEADER_LINE_RE) || []).length;
    return headers >= 2 || LEADING_HEADER_RE.test(text) || GREETING_RE.test(text.slice(0, 600));
}

function emailShaped(text: string): boolean {
    const headers = (text.slice(0, 2000).match(EMAIL_HEADER_LINE_RE) || []).length;
    return headers >= 2 || LEADING_HEADER_RE.test(text) || GREETING_RE.test(text.slice(0, 600))
        || (SIGN_OFF_RE.test(text) && text.length <= 3000);
}

// ─── What a request says ────────────────────────────────────────────────────

// A request title. A form puts it at the top, an email in its subject. The same
// words further down are usually a field ("Booking reference: Quote Request
// 12345", "Work Order: Example Strata W.O 67890"), so they count only:
//   - as the first line of the text or the email subject (with any RE:/FW:,
//     a flag or a "Reminder -" before them),
//   - in capitals, which is how a form flattened to one line keeps its title,
//   - or straight before the request's own number ("Quote Request - Quote
//     No. 12345", "Quote Request / ID #12345", "WORK ORDER - Job No.012345").
const REQUEST_TITLE = String.raw`(?:client\s+)?(?:(?:quot(?:e|ation)|pricing|tender|proposal)\s*request|request\s+for\s+(?:a\s+)?(?:quot(?:e|ation)|pricing|tender|proposal))\b`;
const AUTHORITY_TITLE = String.raw`(?:client\s+)?(?:works?|purchase|service|job)\s+(?:order|request)\b`;
const TITLE_PREFIX = String.raw`(?:(?:re|fw|fwd)\s*:\s*|\*{2,}[^*\n]{0,20}\*{2,}\s*|(?:reminder|new|urgent|updated|revised|follow[\s-]?up)\s*[-–:|]\s*)*`;
const FIRST_LINE_CHARS = 150;
const SUBJECT_LINE_RE = /^[ \t>]*subject\s*:\s*([^\n]*)$/im;
const NUMBERED_TITLE_RE = /\b(?:(?:quot(?:e|ation))\s+request|(?:works?|purchase|service|job)\s+order)\s*(?:[-–:/]\s*)?(?:(?:quote|job|id)\s*)?(?:no\b\.?|number|#)\s*[:#.]?\s*[A-Z]{0,3}\d/i;
const CAPS_REQUEST_TITLE_RE = /\b(?:CLIENT\s+)?(?:QUOTE|QUOTATION|PRICING|TENDER)\s*REQUEST\b|\bREQUEST\s+FOR\s+(?:A\s+)?(?:QUOTE|QUOTATION|PRICING|TENDER|PROPOSAL)\b/;
const CAPS_AUTHORITY_TITLE_RE = /\b(?:CLIENT\s+)?(?:WORKS?|PURCHASE|SERVICE|JOB)\s+(?:ORDER|REQUEST)\b/;
const OTHER_AUTHORITY_TITLE_RES: RegExp[] = [
    /\bstrata\s+works?\s+agreement\b/i,
    /\byou\s+have\s+been\s+assigned\s+a\s+(?:work|job|service)\s+(?:request|order)\b/i,
];
const INVITATION_TITLE_RE = /\binvitation\s+to\s+(?:quot(?:e|ation)|tender|submit\s+a\s+(?:quot(?:e|ation)|tender|price))\b/i;

function titleAtTop(text: string, head: string, title: string): string | null {
    const startsWith = new RegExp(`^\\s*${TITLE_PREFIX}(${title})`, 'i');
    const firstLine = (text.trimStart().split('\n')[0] ?? '').slice(0, FIRST_LINE_CHARS);
    const subject = head.match(SUBJECT_LINE_RE)?.[1] || '';
    return firstLine.match(startsWith)?.[1] || subject.match(startsWith)?.[1] || null;
}

function requestTitle(text: string, head: string): { formal: string | null; authority: string | null } {
    const numbered = head.match(NUMBERED_TITLE_RE)?.[0] || null;
    const numberedIsAuthority = Boolean(numbered && /order/i.test(numbered));
    const formal = titleAtTop(text, head, REQUEST_TITLE)
        || head.match(CAPS_REQUEST_TITLE_RE)?.[0]
        || (numbered && !numberedIsAuthority ? numbered : null)
        || head.match(INVITATION_TITLE_RE)?.[0]
        || null;
    const authority = titleAtTop(text, head, AUTHORITY_TITLE)
        || head.match(CAPS_AUTHORITY_TITLE_RE)?.[0]
        || (numbered && numberedIsAuthority ? numbered : null)
        || firstMatch(head, OTHER_AUTHORITY_TITLE_RES)
        || null;
    return { formal: formal ? formal.replace(/\s+/g, ' ').trim() : null, authority: authority ? authority.replace(/\s+/g, ' ').trim() : null };
}

// A tender package from a consultant or the owners: conditions of tendering,
// a tender period, a closing date, a response form. Two of them make a request.
const TENDER_PACKAGE_RES: RegExp[] = [
    /\bconditions\s+of\s+(?:tender(?:ing)?|quotation)\b/i,
    /\btender\s+(?:program(?:me)?|period|closing\s+date|closes|close\s+date|schedule|response\s+form|submission\s+form|documents?)\b/i,
    /\bissued\s+for\s+tender\b/i,
    /\btenderers?\s+(?:shall|must|are\s+(?:to|required)|is\s+(?:to|required)|should)\b/i,
    /\btenders?\s+must\s+(?:remain\s+valid|be\s+(?:submitted|lodged|received))\b/i,
    /\binvitation\s+to\s+tender\b/i,
];

// Someone asking the reader for a price ("please quote", "can you quote",
// "please provide a quotation", "we require a quote"). "quote" is the verb asked
// for, so "Please quote the reference number" and "Please quote PO 7781 on your
// invoice" are not asks.
const NOT_A_QUOTE_OBJECT = String.raw`(?!\s+(?:(?:the|this|our|your|my|a|an|any)\s+)?(?:ref(?:erence)?|number|no\.?\s*[:#]?\s*\d+|invoice|account|p\.?o\b|purchase\s+order|order\s+(?:no|number)|job\s+(?:no|number)|claim)\b)`;
const ASK_VERBS = String.raw`(?:(?:inspect|attend|assess|check|investigate|review|look|take\s+a\s+look|have\s+a\s+look|make\s+(?:it\s+)?safe|(?:water|flood)\s+test)(?:\s*,\s*[a-z]+(?:\s+[a-z]+)?)?\s*,?\s+(?:and|&)\s+)?`;
const PRICE_NOUN = String.raw`(?:new\s+|revised\s+|written\s+|formal\s+|separate\s+|detailed\s+|itemi[sz]ed\s+)?(?:quot(?:e|ation)s?|price|pricing|estimate|proposal|tender)`;
const DIRECT_ASK_RES: RegExp[] = [
    // please / kindly / can you / could you (please) (inspect and) quote / price / provide a quote
    new RegExp(String.raw`\b(?:please|kindly|(?:can|could|would|will)\s+you(?:\s+please)?)\s+(?:arrange\s+to\s+)?${ASK_VERBS}(?:(?:provide|give|send|submit|supply|forward)\s+(?:us\s+|me\s+)?(?:with\s+)?(?:a|an|your|the)\s+(?:report\s*(?:&|and)\s*)?${PRICE_NOUN}\b|(?:quote|price)\b${NOT_A_QUOTE_OBJECT})`, 'i'),
    // "Please attend asap to investigate & determine the cause & provide a report & quote"
    /\b(?:please|kindly)\s+(?:attend|inspect|investigate)\b[^.\n]{0,160}?\bprovide\s+(?:us\s+with\s+)?(?:a\s+)?(?:report\s*(?:&|and)\s*)?quot(?:e|ation)\b/i,
    // "was hoping you can provide a pricing", "wondering if you could send a quote"
    /\b(?:hoping|wondering\s+if|asking\s+if)\s+(?:[\w.]+\s+){0,3}(?:can|could|would)\s+(?:please\s+)?(?:provide|give|send|submit)\s+(?:us\s+)?(?:a\s+|an\s+|your\s+)?(?:quot(?:e|ation)|price|pricing|estimate)\b/i,
    // The strata manager's cover note: "Please find attached a quotation request",
    // "Please find the attached document with the details of the quote request".
    /\b(?:please\s+find|attached\s+(?:is|are)|enclosed\s+(?:is|are))\s+(?:attached\s+)?(?:the\s+|a\s+|our\s+)?(?:attached\s+)?(?:document\s+with\s+the\s+details\s+of\s+the\s+)?(?:quot(?:e|ation)|tender|pricing)\s+request\b/i,
    /\b(?:please\s+(?:find\s+)?attached|attached\s+is)\s+(?:the\s+)?(?:scope|brief|specification|rfq)\b/i,
    // "we require a quote", "we would like a quotation", "we are seeking a quote"
    new RegExp(String.raw`\b(?:we|i)\s+(?:require|need|want|would\s+like|are\s+seeking|seek|request)\s+(?:to\s+(?:obtain|receive|get)\s+)?(?:a\s+|an\s+|your\s+)?${PRICE_NOUN}\b`, 'i'),
    // "we kindly request that you provide a quote"
    /\b(?:request|ask)\s+(?:that\s+)?you\s+(?:to\s+)?(?:please\s+)?(?:provide|submit|send|forward|quote)\b/i,
    /\bseeking\s+(?:a\s+|your\s+)?(?:quot(?:e|ation)|proposal|price|tender)\b/i,
    // "This is a Request for a Quotation only", "We request a quotation"
    /\b(?:this\s+is\s+an?\s+)request\s+for\s+(?:a\s+)?(?:quot(?:e|ation)|pricing|proposal|tender)\b/i,
    /\bquotation\s+(?:is\s+)?(?:required|requested|sought)\b/i,
    // "for your quotation", "would be obliged to receive your quote"
    /(?<!thank\s+you\s+|thanks\s+)\bfor\s+your\s+(?:quotation|quote|pricing)\b/i,
    /\b(?:obliged|pleased|grateful)\s+to\s+receive\s+(?:a\s+|an\s+|your\s+)?quot(?:e|ation)\b/i,
    // A sentence that opens with the verb: "Quote both please", "Price to replace please."
    /(?:^|[.!?]\s+|\n\s*)(?:quote|price)\s+(?:to\s+[a-z]+|for|on|both|all|each|it|this|these|them)\b[^.\n]{0,60}?\bplease\b/i,
];
// Someone reported as wanting a price ("The committee would like a quote",
// "Committee wants a price"). From a strata manager this is the ask; in an
// assessor's notes it is a note, so it counts only in a message or with strata
// markers.
const REPORTED_ASK_RES: RegExp[] = [
    /\b(?:committee|owners?|strata|client|customer|tenant|resident)\s+(?:would\s+(?:like|appreciate|be\s+grateful\s+for)|wants?|asks?\s+for|requires?|needs?|has\s+approved\s+getting|is\s+seeking)\s+(?:a\s+|an\s+|your\s+)?(?:quot(?:e|ation)|price|pricing|estimate)s?\b/i,
    /\b(?:would\s+like|wants?)\s+(?:\w+\s+){0,2}quoted\b/i,
    // A question asking the reader to come and look ("can you have a look at the
    // leak in unit 9 please"). An instruction ("Please attend and inspect") is
    // the office briefing its own assessor, so only the question counts.
    /\b(?:can|could|would)\s+you\s+(?:please\s+)?(?:(?:have|take)\s+a\s+look\s+at|look\s+(?:at|into)|attend\s+to|inspect|investigate|assess|come\s+(?:out|and\s+(?:look|see|inspect)))\b/i,
];

// Markers of a strata or building manager's request.
const STRATA_MARKER_RES: RegExp[] = [
    /\b(?:strata\s+manager|building\s+manager|owners?\s+corp(?:oration)?|body\s+corp(?:orate)?|strata\s+committee|property\s+manager|facilities\s+manager|asset\s+manager|strata\s+management|strata\s+services)\b/i,
    /\b(?:the\s+)?owners?\s*(?:[-–]+\s*|of\s+)?strata\s+plan\b|\bstrata\s+plan\s*(?:no\.?|#)?\s*:?\s*\d{2,}|\bS\.?\s?P\.?\s?\d{3,}\b/i,
    /\bcommittee\b/i,
    /\border\s+by\s*:|\bsite\s+contact\b|\baccess\s+contact\b|\bcontact\s+for\s+access\b|\bquote\s+(?:to\s+be\s+)?addressed\s+to\b/i,
];

// ─── What a request is not ──────────────────────────────────────────────────

// A quote, a tender or its cover note, sent by whoever prices the work.
const QUOTE_OFFER_RE = /\b(?:has\s+(?:much\s+)?pleasure\s+in\s+(?:submitting|providing|presenting)|(?:are|is)\s+pleased\s+to\s+(?:submit|provide|present|offer|attach)|thank\s+you\s+for\s+the\s+opportunity\s+to\s+(?:quote|tender|price|provide)|(?:please\s+find(?:\s+attached|\s+enclosed)?|attached\s+(?:is|are)|enclosed\s+(?:is|are)|i\s+have\s+attached|we\s+have\s+attached)\s+(?:our|my)\s+(?:revised\s+|updated\s+|amended\s+)?(?:quote|quotation|tender|proposal|pricing)\b(?!\s+request)|we\s+(?:hereby\s+)?(?:submit|offer)\s+(?:our|the\s+following))\b/i;
const QUOTE_VALIDITY_RE = /\bvalid(?:ity)?\b[^\n]{0,25}\b\d+\s*(?:calendar\s+)?days?\b|\b(?:this\s+)?quot(?:e|ation)\s+is\s+valid\b|\bprices?\s+(?:are|is)\s+valid\b/i;
const QUOTE_TOTAL_RE = /\btotal\s+price\b|\btotal\s*\(?\s*(?:inc|incl|including|ex|excl|excluding)\.?\s*gst\b|\bsub-?\s?total\b|\bgrand\s+total\b|\blump\s+sum\s+(?:price|total|amount)\b|\bcontract\s+(?:sum|price)\b|\btotal\s+(?:amount|cost|tender\s+price)\b|\bgst\s+inclusive\s+total\b/i;
const QUOTE_REFERENCE_RE = /\bquot(?:e|ation)\s+reference\s+(?:no|number)\b|\bour\s+quot(?:e|ation)\s+(?:no|number|ref)\b|\bquot(?:e|ation)\s*(?:no\b\.?|number|#)\s*[:#.]?\s*[a-z]{0,4}[\s/-]*\d/i;
const QUOTE_ACCEPTANCE_RE = /\bto\s+accept\s+this\s+(?:quote|quotation|tender|offer)\b|\bacceptance\s+of\s+(?:this\s+)?(?:quote|quotation|tender|offer)\b|\bclient\s+acceptance\b|\bauthority\s+to\s+proceed\b|\bsigned\s+for\s+and\s+on\s+behalf\b|\bbanking\s+details\b|\bbsb\s*[:#]?\s*\d|\bdeposit\s+(?:on\s+acceptance|required|invoice)\b|\bpayment\s+(?:terms|schedule)\b/i;
const TOTAL_LINE_RE = /(?:^|\n)[ \t]*(?:sub-?\s?)?total\b[^\n$]{0,30}\$\s*\d/i;
const QUOTE_NOTES_RE = /\bquot(?:e|ation)\s+notes?\b/i;
// "$20m" (an insurance cover in a request) is not a price.
const MONEY_RE = /(?:\$|\bA\$|\bAUD\s?)\s*\d[\d,]*(?:\.\d{2})?(?!\d|[,.]\d|\s*(?:m|mil|million)\b)/gi;

// Without an offer line, a quote scores 3 or more: a validity period, a total
// or acceptance terms, and one more mark.
const QUOTE_SCORE_MIN = 3;

function quoteSignals(text: string, titled: boolean): { score: number; signals: string[] } {
    const signals: string[] = [];
    let score = 0;
    const mark = (points: number, signal: string) => { score += points; signals.push(signal); };
    const validity = text.match(QUOTE_VALIDITY_RE)?.[0];
    if (validity) mark(2, `states how long a price is valid ("${validity.trim()}")`);
    const total = text.match(QUOTE_TOTAL_RE)?.[0];
    if (total) mark(2, `carries a price total ("${total.trim()}")`);
    const acceptance = text.match(QUOTE_ACCEPTANCE_RE)?.[0];
    if (acceptance) mark(2, `has acceptance or payment terms ("${acceptance.trim()}")`);
    const totalLine = text.match(TOTAL_LINE_RE)?.[0];
    if (!total && totalLine) mark(2, `has a total line ("${totalLine.replace(/\s+/g, ' ').trim()}")`);
    // A request form carries its own "Quote No."; only an untitled text counts it.
    const reference = !titled ? text.match(QUOTE_REFERENCE_RE)?.[0] : null;
    if (reference) mark(1, `carries a quote number ("${reference.trim()}")`);
    const quoteNotes = text.match(QUOTE_NOTES_RE)?.[0];
    if (quoteNotes) mark(1, `has a quote notes section ("${quoteNotes.trim()}")`);
    // A request asks for prices; the requests measured on RJ Bird's documents
    // carried two money figures at most (a budget, an approval limit).
    const prices = (text.match(MONEY_RE) || []).length;
    if (prices >= 3) mark(prices >= 6 ? 3 : prices >= 4 ? 2 : 1, `${prices} prices`);
    return { score, signals };
}

// A report names itself in its title and has report sections.
const REPORT_TITLE_RES: RegExp[] = [
    /\bsite\s+(?:documentation|inspection|visit|investigation)\s+(?:report|summary)\b/i,
    /\bsite\s+report\b/i,
    /\binspection\s+(?:and\s+)?report\b|\binspection\s+summary\b|\bcondition\s+report\b/i,
    /\b(?:engineer(?:'?s|ing)?|structural|building|roof|dilapidation|defects?|investigation|assessment|hazardous\s+materials?|asbestos|moisture|leak\s+detection|thermal(?:\s+imaging)?|test|testing|laboratory|fire\s+safety|compliance|remediation|concrete\s+remediation)\s+(?:inspection\s+)?(?:report|assessment|register|survey)\b/i,
    /\breport\s+(?:no|number|date|ref(?:erence)?)\b|\bcertificate\s+of\s+analysis\b|\basbestos\s+register\b|\bmanagement\s+plan\b/i,
];
const REPORT_SECTION_RES: RegExp[] = [
    /\bexecutive\s+summary\b/i,
    /(?:^|\n)\s*(?:\d+(?:\.\d+)*\.?\s+)?introduction\b/i,
    /(?:^|\n)\s*(?:\d+(?:\.\d+)*\.?\s+)?background\b/i,
    /\bscope\s+of\s+(?:the\s+)?(?:inspection|report|assessment|site\s+visit)\b/i,
    /(?:^|\n)\s*(?:\d+(?:\.\d+)*\.?\s+)?(?:findings|observations?)\b/i,
    /(?:^|\n)\s*(?:\d+(?:\.\d+)*\.?\s+)?recommendations?\b/i,
    /(?:^|\n)\s*(?:\d+(?:\.\d+)*\.?\s+)?conclusions?\b/i,
    /(?:^|\n)\s*(?:\d+(?:\.\d+)*\.?\s+)?(?:limitations|methodology|disclaimer)\b/i,
    /\bareas?\s+inspected\b|\bsite\s*(?:&|and)\s*visit\s+details\b|\bdate\s+(?:attended|of\s+inspection|inspected)\b|\binspected\s+by\b|\bpresent\s+at\s+inspection\b/i,
    /\bprepared\s+by\b|\breport\s+completed\b|\bopinion\s*\/\s*consideration\b/i,
    /\bphoto(?:graph)?s?\s*\(?\s*\d+\s*\)?\s*[:-]|\bfigure\s+\d+\b|\b(?:photos?|images?)\s+\d+\s+(?:to|-)\s+\d+\b/i,
    /(?:^|\n)\s*(?:\d+(?:\.\d+)*\.?\s+)?results?\b|\btesting\s+method\b|\badvisory\s+notes\b/i,
    /\b(?:this|the\s+present)\s+report\b/i,
];
// A report title this close to the start is the document's own title.
const REPORT_TITLE_AT_START_CHARS = 150;

// A builder's letterhead: a builder licence number at the top. The document was
// written by a builder (its quote, report, letter or inspection request), not
// by the client asking for a price. A request form names the contractor it is
// sent to, so a licence just after "Contractor", "Supplier" or "Issued to" does
// not count.
const LETTERHEAD_CHARS = 400;
const BUILDER_LICENCE_RE = /\bbuilder'?s?\s*lic(?:ence|ense)?\.?\s*(?:no\b\.?|number|#)?\s*[:-]?\s*\d{4,}[a-z]?\b|\blic(?:ence|ense)?\.?\s*[-–]?\s*no\b\.?\s*[:-]?\s*\d{4,}[a-z]?\b/i;
const ADDRESSEE_LABEL_RE = /\b(?:contractor|supplier|issued\s+to|attention\s+to|attn|trade|vendor)\b/i;

function builderLetterhead(text: string): string | null {
    const top = text.slice(0, LETTERHEAD_CHARS);
    const match = top.match(BUILDER_LICENCE_RE);
    if (!match || match.index === undefined) return null;
    const before = top.slice(Math.max(0, match.index - 60), match.index);
    return ADDRESSEE_LABEL_RE.test(before) ? null : match[0].replace(/\s+/g, ' ').trim();
}

// The office's own site inspection request to its assessor ("Site Inspection
// Request", "SDT Inspection Request"). It quotes the client's request, so it
// carries the request's number and often its words; it is still not the
// client's request.
const INSPECTION_REQUEST_TITLE_RE = /\b(?:site|sdt)\s+(?:inspection|visit)\s+request\b/i;

// The top message of an email thread: everything before the first quoted
// message header ("From: ... Sent:", "-----Original Message-----", "On ... wrote:").
const QUOTED_MESSAGE_RE = /(?:^|\s)(?:from\s*:[^\n]{0,160}?\s(?:sent|date)\s*:|-{2,}\s*(?:original|forwarded)\s+message\s*-{2,}|on\s+[^\n]{5,80}?\s+wrote\s*:)/i;
// Written by whoever prices the work, replying to the request: "Thank you for
// your email and consideration for us to provide a quote", "We will attend and
// provide our quotation".
const REPLY_FROM_QUOTER_RE = /\bthank\s+you\s+for\s+(?:your\s+(?:email|enquiry|inquiry|consideration|request)|(?:considering|contacting|choosing|engaging)\s+us)\b|\bfor\s+us\s+to\s+(?:provide|quote|attend|price|submit)\b|\bwe\s+(?:will|shall|can|would\s+be\s+(?:happy|pleased|glad))\s+(?:to\s+)?(?:attend|provide|send|forward|prepare|arrange|submit|organi[sz]e)\b[^.\n]{0,60}?\b(?:quot(?:e|ation)|price|pricing|inspection|visit)\b/i;

function topMessage(text: string): string {
    const skip = Math.min(150, text.length);
    const rest = text.slice(skip);
    const match = rest.match(QUOTED_MESSAGE_RE);
    return match && match.index !== undefined ? text.slice(0, skip + match.index) : text;
}

const INVOICE_RES: RegExp[] = [
    /\btax\s+invoice\b/i,
    /\binvoice\s*(?:no\b\.?|number|#)\s*[:#.]?\s*[a-z]{0,4}[\s/-]*\d/i,
    /\bamount\s+(?:due|owing|payable)\b|\bbalance\s+(?:due|owing)\b|\bpayment\s+due\b|\bremittance\s+advice\b|\bstatement\s+of\s+account\b/i,
];

const PLAN_MARKER_RES: RegExp[] = [
    /\bdrawing\s+(?:no\b\.?|number|title|list|register)\b/i,
    /\bscale\s*(?:[:=]\s*)?1\s*[:/]\s*\d+\b/i,
    /\bdo\s+not\s+scale\b/i,
    /\bdrawn\s*(?:by)?\s*[:.]/i,
    /\bchecked\s*(?:by)?\s*[:.]/i,
    /\bsheet\s+\d+\s+of\s+\d+\b|\bsheet\s+(?:no\b\.?|number)\b/i,
    /\b(?:cover\s+sheet|site\s+plan|floor\s+plan|demo(?:lition)?\s+plan|section\s+[a-z]-[a-z]|elevations?\s+[a-z0-9])\b/i,
];

// ─── The detector ───────────────────────────────────────────────────────────

function notRfq(rejectedAs: RfqContentRejection | null, evidence: string[]): RfqContentResult {
    return { isRfq: false, kind: null, rejectedAs, title: null, authority: false, evidence };
}

/**
 * Whether a text is a client's request for a quote, read from the text alone.
 * Never reads a file name, an extension or a folder.
 */
export function detectRfqByContent(input: unknown): RfqContentResult {
    const raw = typeof input === 'string' ? input : '';
    const text = normalise(raw);
    if (text.replace(/\s+/g, '').length < 20) return notRfq(null, ['too little text to read']);
    const head = text.slice(0, HEAD_CHARS);
    // An email subject names what the message is about ("engineer's report
    // east balconies"), not what the text is, so it is not a title here.
    const titleZone = text.slice(0, TITLE_CHARS).replace(/^[ \t>]*subject\s*:[^\n]*$/gim, '');
    const isEmail = emailShaped(text);

    const { formal: formalTitle, authority: authorityTitle } = requestTitle(text, head);
    const titled = Boolean(formalTitle || authorityTitle);
    const askWindow = isEmail ? text : text.slice(0, ASK_WINDOW_CHARS);
    const directAsk = firstMatch(askWindow, DIRECT_ASK_RES);
    const reportedAsk = firstMatch(askWindow, REPORTED_ASK_RES);
    const strataMarkers = countMatches(text, STRATA_MARKER_RES);

    // ── What a request is not ──────────────────────────────────────────────
    const notes = detectEstimatorPricedNotes(raw);
    if (notes.isEstimatorNotes) {
        return notRfq('estimator-notes', [`it reads as the estimator's own priced notes (${notes.evidence.join('; ')})`]);
    }

    const offer = text.match(QUOTE_OFFER_RE)?.[0];
    if (offer) return notRfq('quote', [`it offers a price, as a quote or its cover note does ("${offer.replace(/\s+/g, ' ').trim()}")`]);

    // A tender package asks for a price by its conditions of tendering, even
    // when it also states a validity period or a contract sum.
    const tenderMarkers = countMatches(text, TENDER_PACKAGE_RES);
    if (tenderMarkers.length >= 2) {
        return { isRfq: true, kind: 'formal-form', rejectedAs: null, title: null, authority: false, evidence: [`it is a tender package (${tenderMarkers.slice(0, 3).join(', ')})`] };
    }

    const quote = quoteSignals(text, titled);
    if (quote.score >= QUOTE_SCORE_MIN) {
        return notRfq('quote', [`it reads as a quote: ${quote.signals.join('; ')}`]);
    }

    const letterhead = builderLetterhead(text);
    if (letterhead) {
        return notRfq('builder-document', [`it opens with a builder's letterhead ("${letterhead}"), so a builder wrote it`]);
    }

    // An email that asks for a quote may call itself an inspection request in
    // its body ("this is a site inspection request, please inspect and quote");
    // only a form titled so is the office's own.
    const inspectionRequest = titleZone.match(INSPECTION_REQUEST_TITLE_RE)?.[0];
    if (inspectionRequest && !(addressedEmail(text) && directAsk)) {
        return notRfq('inspection-request', [`it is the office's site inspection request to its assessor ("${inspectionRequest}")`]);
    }

    // A message that asks the reader for a price is the request even when it
    // names a report ("engineer's report attached, please quote").
    const reportTitleMatch = earliestMatch(titleZone, REPORT_TITLE_RES);
    const reportTitle = reportTitleMatch ? reportTitleMatch.text : null;
    const requestTitleAt = titled ? titleZone.search(wordsPattern(String(formalTitle || authorityTitle))) : -1;
    // A message names the report it carries ("Engineer's report attached"); a
    // report opens with its own title.
    const titledAsReport = !isEmail && Boolean(reportTitleMatch) && reportTitleMatch!.index < REPORT_TITLE_AT_START_CHARS
        && (requestTitleAt < 0 || reportTitleMatch!.index < requestTitleAt);
    const reportSections = countMatches(text, REPORT_SECTION_RES);
    const messageAsks = isEmail && Boolean(directAsk || reportedAsk);
    const reportByTitle = titledAsReport || (Boolean(reportTitle) && reportSections.length >= 2);
    if (!messageAsks && (reportByTitle || reportSections.length >= 4)) {
        const why = reportTitle ? `titled "${reportTitle}"` : 'no title';
        return notRfq('report', [`it reads as a report (${why}; sections: ${reportSections.slice(0, 4).join(', ') || 'none'})`]);
    }

    const top = isEmail ? topMessage(text) : '';
    const reply = top ? top.match(REPLY_FROM_QUOTER_RE)?.[0] : null;
    if (reply && !firstMatch(top, DIRECT_ASK_RES)) {
        return notRfq('reply', [`the message is the quoting business replying to a request ("${reply.replace(/\s+/g, ' ').trim()}")`]);
    }

    const invoice = firstMatch(head, INVOICE_RES);
    if (invoice) return notRfq('invoice', [`it reads as an invoice or statement ("${invoice}")`]);

    const planMarkers = countMatches(text, PLAN_MARKER_RES);
    if (!isEmail && planMarkers.length >= 2) {
        return notRfq('plan', [`it reads as a drawing (${planMarkers.slice(0, 3).join(', ')})`]);
    }

    // ── What a request says ────────────────────────────────────────────────
    if (formalTitle || authorityTitle) {
        const title = formalTitle || authorityTitle;
        return {
            isRfq: true,
            kind: 'formal-form',
            rejectedAs: null,
            title,
            authority: !formalTitle,
            evidence: [`it is titled as a request ("${title}")`, ...(directAsk ? [`it asks for a price ("${directAsk}")`] : [])],
        };
    }
    const ask = directAsk || ((isEmail || strataMarkers.length > 0) ? reportedAsk : null);
    if (ask && strataMarkers.length > 0) {
        return {
            isRfq: true,
            kind: 'strata-request',
            rejectedAs: null,
            title: null,
            authority: false,
            evidence: [`it asks for a price ("${ask}")`, `strata markers: ${strataMarkers.slice(0, 3).join(', ')}`],
        };
    }
    if (ask) {
        return {
            isRfq: true,
            kind: 'email-ask',
            rejectedAs: null,
            title: null,
            authority: false,
            evidence: [`it asks for a price ("${ask}")`],
        };
    }
    return notRfq(null, ['no request title and no ask for a price']);
}

// ─── Shared with the report and drawing detectors (CN3) ─────────────────────
// Exported as they are, so the report and drawing detectors read a text the
// same way this one does. Exporting changes nothing above. The scope of works
// detector (CN4) reads the tender package marks the same way.
export {
    normalise as normaliseDetectorText,
    earliestMatch,
    countMatches,
    INVOICE_RES,
    PLAN_MARKER_RES,
    REPORT_SECTION_RES,
    REPORT_TITLE_AT_START_CHARS,
    REPORT_TITLE_RES,
    TENDER_PACKAGE_RES,
    TITLE_CHARS,
};
